import { fetchInstance } from '@/utils/fetchInstance.ts';

export interface PokePlayerStats {
  pokePlayerStatsId: string;
  userId: string;
  latestCatchingDate: string;
  nextCatchingDate: string;
  catchCharges: number;
  catchChargeLimit: number;
  pokeDollars: number;
  commonProbability: number;
  unCommonProbability: number;
  rareProbability: number;
  epicProbability: number;
  legendaryProbability: number;
  pokedexProg: number;
  createdAt: string;
  updatedAt: string;
  totalCaught?: number;
  catchStreak?: number;
  maxCatchStreak?: number;
  shinyCount?: number;
  legendaryCount?: number;
  expeditionsWon?: number;
}

interface PokemonEvolvesTo {
  id: string;
  trigger: string;
  level?: number;
  item?: string;
}

interface PokemonAttack {
  levelToLearn: number;
  attackId: number;
}

export interface Pokemon {
  id: string;
  pokedexId: number;
  name: string;
  realname: string;
  starter: boolean;
  legendary: boolean;
  sprite: string;
  spriteShiny: string;
  spriteAnimation: string;
  spriteAnimationShiny: string;
  origin: string | null;
  stats: {
    pv: number;
    attack: number;
    defense: number;
    attackspe: number;
    defensespe: number;
    speed: number;
    total: number;
  };
  types: string[];
  forms: string[];
  megas: string[];
  gigamax: string[];
  height: number;
  weight: number;
  generation: string;
  probability: number;
  levels: number[];
  description: string;
  evolution: { stage: number; evolvesTo?: PokemonEvolvesTo[] };
  attacks: PokemonAttack[];
}

export interface OwnedPokemonAttack {
  id: number;
  name: string;
  realname: string;
  type: string;
  category: 'physical' | 'special' | 'status';
  power: number | null;
  accuracy: number | null;
  pp: number;
}

export interface HeldItem {
  id: number;
  type: string;
  key: string;
  name: string;
  icon: string;
  price: { pokedollars: number; b2coins: number };
}

export interface OwnedPokemon {
  ownedPokemonId: string;
  userId: string;
  pokemonId: string;
  nickname: string;
  isShiny: boolean;
  level: number;
  hp: number;
  hpMax: number;
  happiness: number;
  formName: string;
  rarity: number;
  attack1: number | null;
  attack2: number | null;
  attack3: number | null;
  attack4: number | null;
  attacks: OwnedPokemonAttack[];
  heldItemId: number | null;
  heldItem: HeldItem | null;
  ivHp: number;
  ivAtk: number;
  ivDef: number;
  ivSpAtk: number;
  ivSpDef: number;
  ivSpe: number;
  evHp: number;
  evAtk: number;
  evDef: number;
  evSpAtk: number;
  evSpDef: number;
  evSpe: number;
  isFavorite: boolean;
  teamSlot: number | null;
  createdAt: string;
  updatedAt: string;
  pokemon: Pokemon;
}

interface GetOwnedPokemonsResponse {
  data: OwnedPokemon[];
  total: number;
  limit: number;
  offset: number;
}

interface GetOwnedPokemonsParams {
  offset?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'level' | 'nickname' | 'rarity' | 'pokedexId' | 'ivAverage';
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  onlyFavorites?: boolean;
  onlyShiny?: boolean;
  onlyLegendary?: boolean;
}

export interface TeamSlot {
  slot: number;
  pokemon: OwnedPokemon | null;
}

export const getPokePlayerStats = async () => {
  return await fetchInstance.get<PokePlayerStats>('pokemons/stats');
};

export interface PublicOwnedPokemon {
  ownedPokemonId: string;
  userId: string;
  pokemonId: string;
  nickname: string | null;
  isShiny: boolean;
  level: number;
  createdAt: string;
  pokemon: Pokemon;
}

interface GetPublicUserPokemonsResponse {
  data: PublicOwnedPokemon[];
  total: number;
  limit: number;
  offset: number;
}

interface GetPublicUserPokemonsParams {
  offset?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'level' | 'nickname' | 'rarity' | 'pokedexId' | 'ivAverage';
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  onlyShiny?: boolean;
  onlyLegendary?: boolean;
}

export const getPublicUserPokemons = async (
  username: string,
  {
    offset = 0,
    limit = 20,
    sortBy = 'createdAt',
    sortOrder = 'DESC',
    search,
    onlyShiny,
    onlyLegendary,
  }: GetPublicUserPokemonsParams = {}
) => {
  return await fetchInstance.get<GetPublicUserPokemonsResponse>(
    `pokemons/users/${encodeURIComponent(username)}`,
    {
      params: {
        offset, limit, sortBy, sortOrder,
        ...(search ? { search } : {}),
        ...(onlyShiny ? { onlyShiny: 'true' } : {}),
        ...(onlyLegendary ? { onlyLegendary: 'true' } : {}),
      },
    }
  );
};

export const getOwnedPokemons = async ({
  offset = 0,
  limit = 20,
  sortBy = 'createdAt',
  sortOrder = 'DESC',
  search,
  onlyFavorites,
  onlyShiny,
  onlyLegendary,
}: GetOwnedPokemonsParams = {}) => {
  return await fetchInstance.get<GetOwnedPokemonsResponse>('pokemons/pc', {
    params: {
      offset,
      limit,
      sortBy,
      sortOrder,
      ...(search ? { search } : {}),
      ...(onlyFavorites ? { onlyFavorites: 'true' } : {}),
      ...(onlyShiny ? { onlyShiny: 'true' } : {}),
      ...(onlyLegendary ? { onlyLegendary: 'true' } : {}),
    },
  });
};

export const toggleFavorite = async (ownedPokemonId: string) => {
  return await fetchInstance.patch<{ isFavorite: boolean }>(
    `pokemons/${ownedPokemonId}/favorite`,
  );
};

export const setTeamSlot = async (ownedPokemonId: string, slot: number | null) => {
  return await fetchInstance.patch<{ teamSlot: number | null }>(
    `pokemons/${ownedPokemonId}/team-slot`,
    { slot },
  );
};

export const getMyTeam = async () => {
  return await fetchInstance.get<TeamSlot[]>('pokemons/team');
};

// ─── Shared evolution / move types ────────────────────────────────────────────

export interface EvolutionData {
  allyOwnedPokemonId: string;
  fromPokemonId: string;
  fromName: string;
  evolvedPokemonId: string;
  evolvedPokemonName: string;
}

export interface PendingMoveData {
  attackId: number;
  realname: string;
}

// ─── Ultra Brèche ────────────────────────────────────────────────────────────

export interface UltraBrecheEnemy {
  realname: string;
  level: number;
  types: string[];
  shiny: boolean;
  sprite: string;
}

export interface UltraBrecheData {
  status: 'pending' | 'active' | 'won' | 'lost' | 'fled' | 'caught' | string;
  enemy: UltraBrecheEnemy | null;
}

export const getUltraBreche = (webToken: string) =>
  fetchInstance.get<UltraBrecheData>(`pokemons/ultra-breche/${webToken}`);

// ─── Held Item ────────────────────────────────────────────────────────────────

export type EquipHeldItemResponse =
  | { action: 'equipped'; heldItemId: number; heldItem: HeldItem }
  | { action: 'evolved'; allyOwnedPokemonId: string; fromName: string; evolvedName: string; evolvedPokemonId: string; isNewInPokedex: boolean; pokeDollarsEarned: number }
  | { action: 'ribbon_applied'; happinessGained: number; newHappiness: number };

export const equipHeldItem = (ownedPokemonId: string, itemId: number) =>
  fetchInstance.put<EquipHeldItemResponse>(`pokemons/${ownedPokemonId}/held-item`, { itemId });

export type ConfirmEvolutionResponse = { fromName: string; evolvedName: string; isNewInPokedex: boolean; pokeDollarsEarned: number };

export const confirmEvolution = (ownedPokemonId: string, evolvedPokemonId: string): Promise<ConfirmEvolutionResponse> =>
  fetchInstance.post<ConfirmEvolutionResponse>(`pokemons/${ownedPokemonId}/evolve`, { evolvedPokemonId });

export const refuseEvolution = (ownedPokemonId: string): Promise<void> =>
  fetchInstance.delete<void>(`pokemons/${ownedPokemonId}/evolve`);

export interface LearnMoveResponse {
  learned: boolean;
  forgottenName?: string;
  newAttackName?: string;
}

export const learnMove = (ownedPokemonId: string, attackId: number, slotIndex: number): Promise<LearnMoveResponse> =>
  fetchInstance.post<LearnMoveResponse>(`pokemons/${ownedPokemonId}/learn-move`, { attackId, slotIndex });

export const removeHeldItem = (ownedPokemonId: string) =>
  fetchInstance.delete<void>(`pokemons/${ownedPokemonId}/held-item`);

// ─── Super Bonbon ─────────────────────────────────────────────────────────────

export interface UseSuperBonbonResponse {
  newLevel: number;
  evolution?: EvolutionData;
  pendingMove?: PendingMoveData;
}

export const useSuperBonbon = (ownedPokemonId: string): Promise<UseSuperBonbonResponse> =>
  fetchInstance.post<UseSuperBonbonResponse>('items/super-bonbon/use', { ownedPokemonId });

// ─── Pokédex ──────────────────────────────────────────────────────────────────

export interface PokedexEntry {
  id: string;
  pokedexId: number;
  name: string;
  realname: string;
  types: string[];
  generation: string | null;
  rarity: number;
  sprite: string;
  isCaught: boolean;
  caughtAt: string | null;
}

export interface PokedexResponse {
  data: PokedexEntry[];
  total: number;
  caughtCount: number;
  totalPokemon: number;
  offset: number;
  limit: number;
}

export interface PokedexGenerationStat {
  generation: string;
  caught: number;
  total: number;
}

interface GetPokedexParams {
  offset?: number;
  limit?: number;
  search?: string;
  type?: string;
  caught?: 'all' | 'caught' | 'uncaught';
}

export const getPokedex = ({
  offset = 0,
  limit = 25,
  search,
  type,
  caught = 'all',
}: GetPokedexParams = {}): Promise<PokedexResponse> =>
  fetchInstance.get<PokedexResponse>('pokemons/pokedex', {
    params: {
      offset,
      limit,
      caught,
      ...(search ? { search } : {}),
      ...(type ? { type } : {}),
    },
  });

export const getPokedexGenerations = (): Promise<PokedexGenerationStat[]> =>
  fetchInstance.get<PokedexGenerationStat[]>('pokemons/pokedex/generations');

// ─── Species detail ───────────────────────────────────────────────────────────

export interface SpeciesEvolution {
  id: string;
  trigger: string;
  level?: number;
  item?: string;
  realname: string;
  sprite: string;
}

export interface EvolutionChainNode {
  id: string;
  realname: string;
  sprite: string;
  evolvesTo: { trigger: string; level?: number; item?: string; node: EvolutionChainNode }[];
}

export interface PokemonSpecies {
  id: string;
  description: string;
  evolution: {
    stage: number;
    evolvesTo: SpeciesEvolution[];
  };
  fullChain: EvolutionChainNode | null;
}

export const getPokemonSpecies = (speciesId: string): Promise<PokemonSpecies> =>
  fetchInstance.get<PokemonSpecies>(`pokemons/species/${speciesId}`);

// ─── Leaderboard Pokédex ──────────────────────────────────────────────────────

export interface PokedexLeaderboardEntry {
  rank: number;
  username: string;
  pokedexCount: number;
}

export const getPokedexLeaderboard = (): Promise<PokedexLeaderboardEntry[]> =>
  fetchInstance.get<PokedexLeaderboardEntry[]>('pokemons/leaderboard');

// ─── Release ──────────────────────────────────────────────────────────────────

export interface ReleaseResponse {
  pokeDollarsEarned: number;
  newBalance: number;
}

export const releasePokemon = (ownedPokemonId: string) =>
  fetchInstance.delete<ReleaseResponse>(`pokemons/release/${ownedPokemonId}`);

// ─── Fusion ───────────────────────────────────────────────────────────────────

export interface FusionResponse {
  ownedPokemonId: string;
}

export const fusionPokemon = (sourcePokemonId: string, targetPokemonId: string): Promise<FusionResponse> =>
  fetchInstance.post<FusionResponse>('pokemons/fusion', { sourcePokemonId, targetPokemonId });

export const getFusionCandidates = (): Promise<{ data: OwnedPokemon[] }> =>
  fetchInstance.get<{ data: OwnedPokemon[] }>('pokemons/fusion');
